import { FunctionComponent, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useInitializationEffect, useValidatedParam } from 'core/hooks';

import { AuthenticationScheme, useAuthentication, useParsedShareAccessToken, useRouteGuard } from 'features/auth';

export const ShareDefaultAuthorizationChallenge: FunctionComponent = () => {
  const navigate = useNavigate();
  const { activeScheme, logoutShare } = useAuthentication();
  const { allowedSchemes } = useRouteGuard();
  const linkId = useValidatedParam('linkId', 'guid', false);
  const shareToken = useParsedShareAccessToken(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  if (!allowedSchemes.includes(AuthenticationScheme.SHARE))
    throw new Error(
      'Authorization configuration error.  <ShareDefaultAuthorizationChallenge /> requires that the "SHARE" scheme be specified as a property on the parent <RouteGuard /> component.',
    );

  useEffect(() => {
    // Handle share link switching - if user is already in SHARE scheme but with different linkId
    if (activeScheme === AuthenticationScheme.SHARE && shareToken != null && linkId != null && shareToken.shareLinkId !== linkId) {
      console.log('ShareDefaultAuthorizationChallenge: Switching share links, logging out current share');
      setIsLoggingOut(true);
      logoutShare();
    }
  }, [activeScheme, shareToken, linkId, logoutShare]);

  useEffect(() => {
    // Wait for logout to complete, then redirect to landing
    if (isLoggingOut && activeScheme !== AuthenticationScheme.SHARE) {
      setIsLoggingOut(false);
      if (linkId != null) {
        const returnRoute = `${location.pathname}${location.search}${location.hash}`;
        navigate(`/share/${linkId}/landing?return=${encodeURIComponent(returnRoute)}`);
      }
    }
  }, [isLoggingOut, activeScheme, linkId, navigate]);

  useInitializationEffect(() => {
    if (activeScheme !== AuthenticationScheme.SHARE) {
      // Redirect to the share authentication page if possible.
      if (linkId != null) {
        const returnRoute = `${location.pathname}${location.search}${location.hash}`;
        navigate(`/share/${linkId}/landing?return=${encodeURIComponent(returnRoute)}`);
      }
    }
  });

  // Render nothing while logging out
  if (isLoggingOut) {
    return null;
  }

  return null;
};

ShareDefaultAuthorizationChallenge.displayName = 'ShareDefaultAuthorizationChallenge';
